import { ClientEnvironment } from "./ClientEnvironment";
import { config } from "./config";
import { UndoRedoStack } from "./UndoRedoStack";
import { startWith } from "libs/rxjs-operators";
import { combineLatest, distinctUntilChanged, filter, map, of, pairwise, switchMap } from "rxjs";
import { ServiceWorkerService } from "./service-worker/ServiceWorkerService";
import { getUAParser, isDesktopBrowser, isSingleTabClient, isPWA, isSharedWorkerSupported } from "~/utils/dom-helpers";
import { Logger } from "libs/logger";
import { handleApiVersionError } from "~/utils/handleApiVersionError";
import { AuthService } from "./auth.service";
import { createEnvironmentBase } from "./createEnvironmentBase";
import { SharedWorkerService, SharedWorkerServiceApi } from "./SharedWorkerService";
import { PersistedDatabaseServiceProvider } from "./PersistedDatabaseService";
import { LeadershipService } from "./LeadershipService";
import { PersistedDatabaseWorkerApi } from "./persisted-database-worker/PersistedDatabaseWorkerApi";
import { MessagePortService, PostMessageError } from "./MessagePortService";
import { SyncServiceProvider } from "./shared-worker/SyncService";
import { getClientId } from "~/utils/getClientId";
import { KBarState } from "~/dialogs/kbar";
import { PersistedDatabaseWorkerService } from "./PersistedDatabaseWorkerService";
import { promiseRetryOnError, promiseTimeout } from "libs/promise-utils";
import { isDataCloneError, isEqual, isNonNullable } from "libs/predicates";
import { pick } from "lodash-comms";
import { datadogLogs } from "@datadog/browser-logs";
import { isNativeIOS } from "~/utils/pwaBuilder-utils";
import {
  createClientLogger,
  getContextFromLogEvent,
  getLogLevel,
  sendLogToConsole,
  sendLogToDatadog,
} from "./createClientLogger";
import { IsLoadingService } from "./is-loading.service";
import { IsPendingUpdateService } from "./is-pending-update.service";
import { IsLoadingDialogState } from "~/dialogs/LoadingModal";
import { datadogRum } from "@datadog/browser-rum";
import { RecordValue } from "libs/schema";
import { InputModeService } from "./input-mode.service";
import { SharedSessionStorageKVStore } from "~/utils/KVStore";
import { AnalyticsService } from "./analytics.service";
import { isE2ETest } from "libs/AppEnvironment";
import { Router } from "./router";
import type { TanstackRouter } from "~/routes";

export const logger = createClientLogger({
  name: "MainThread",
  level: getLogLevel() ?? "debug",
  browser: {
    transmit: {
      level: "notice",
      send(_, logEvent) {
        if (logEvent.level.value <= 30) return;

        try {
          const context = getContextFromLogEvent(logEvent);
          sendLogToDatadog(logEvent.level.value, context);
        } catch (error) {
          console.warn("Failed to send log to Datadog", error);
        }
      },
    },
    write: (logObject) => sendLogToConsole((logObject as any).level, logObject),
  },
});

export const isLoading = new IsLoadingService();

export const isPendingUpdate = new IsPendingUpdateService({ isLoading });

export const auth = new AuthService({ logger, isLoading, isPendingUpdate });

export const serviceWorker = new ServiceWorkerService({ logger, auth, isLoading, isPendingUpdate });

export const inputMode = new InputModeService({ logger });

export const leader = new LeadershipService({ logger });

export const sessionStorageService = new SharedSessionStorageKVStore({
  serviceName: "sessionStorage",
  namespace: "sessionStorage",
});

export const analytics = new AnalyticsService({ logger, auth, leader });

export const router = new Router({ logger });

export async function createEnvironment(props: {
  logger: Logger;
  envLogger: Pick<Logger, "debug">;
  tanstackRouter: TanstackRouter;
}) {
  const { logger, envLogger, tanstackRouter } = props;

  envLogger.debug({ config }, "Creating environment...");

  envLogger.debug({ auth }, "Created AuthService");

  envLogger.debug({ serviceWorker }, "Created ServiceWorkerService");

  envLogger.debug({ inputMode }, "Created InputModeService");

  await leader.isReady;
  envLogger.debug({ leader }, "Created LeadershipService");

  const clientId = await getClientId();
  envLogger.debug({ clientId }, "Got clientId");

  // We acquire a context lock on our own clientId. Other tabs can watch this lock
  // to learn when this tab is closed.
  await MessagePortService.acquireContextLock(clientId);
  envLogger.debug({ clientId }, "Acquired clientId context lock");

  const deviceMetadata = {
    device: getUAParser().getDevice(),
    hasSharedWorker: typeof SharedWorker !== "undefined",
    isDesktopBrowser: isDesktopBrowser(),
    isPWA: isPWA(),
    isNativeIOS: isNativeIOS(),
  };

  let sharedWorker: SharedWorkerServiceApi | null = null;
  let persistedDb = null as PersistedDatabaseWorkerApi | null;
  let persistedDbWorker = null as PersistedDatabaseWorkerService | null;

  if (isSingleTabClient() || (isSharedWorkerSupported() && isDesktopBrowser())) {
    sharedWorker = await SharedWorkerService.create(
      { logger, clientId, leader, auth },
      {
        async onGetPersistedDbPortForSharedWorker(request) {
          // In the production logs we saw at least one instance of a DataCloneError being thrown
          // for this request. I'm not sure why (always could have been a random Safari bug). If we
          // run into a similar error we should retry the request.
          await promiseRetryOnError(
            {
              waitMs: 10,
              exponentialBackoff: true,
              maxWaitMs: 500,
              errorFilter: (error) => error instanceof PostMessageError && isDataCloneError(error.cause),
            },
            async () => {
              await persistedDbWorker!.getPortForSharedWorker(request);
            },
          );
        },
      },
    );

    envLogger.debug({ sharedWorker }, "Created SharedWorkerService");

    persistedDbWorker = new PersistedDatabaseWorkerService(
      { logger, clientId, leader, auth },
      {
        onReinstallSchema: async ({ logger }) => {
          try {
            // This will check for a service worker update and, if available, install and activate
            // the update. This will cause all pages to reload with the latest version of the client,
            // if successful. It's very important we do this *before* attempting to reinstall the Comms
            // database so that we have the latest version of the db schema.
            await serviceWorker.checkForUpdateAndActivateIfAvailable();
          } catch (error) {
            logger.error({ error }, "[onReinstallSchema] checkForUpdateAndActivateIfAvailable");
            await auth.signout();
          }
        },
      },
    );

    envLogger.debug({ persistedDbWorker }, "Created PersistedDatabaseWorkerService");

    persistedDb = PersistedDatabaseServiceProvider.createService(
      { logger },
      {
        isConnectionActive: sharedWorker.isActivePromise.promise,
        connection: sharedWorker.connection,
      },
    );

    envLogger.debug({ persistedDb }, "Created PersistedDatabaseService");

    // We want to activate the shared worker after the services which depend on it are activated.
    // Delaying activation causes pending messages sent through the sharedWorker to be buffered
    // and allows all the services which depend on it to be ready to receive messages.
    await sharedWorker.activate();
    envLogger.debug("Activated shared worker");
  } else {
    envLogger.debug(deviceMetadata, "Skipping SharedWorkerService.");
    envLogger.debug(deviceMetadata, "Skipping PersistedDatabaseWorkerService");
    envLogger.debug(deviceMetadata, "Skipping PersistedDatabaseService");
  }

  const environmentBase = await createEnvironmentBase({
    logger,
    envLogger,
    config,
    auth,
    isLoading,
    persistedDb,
    clientId,
    subscribeToAlwaysAvailableRecordsFetchStrategy: "cache",
    onApiAuthenticationError: () => {
      return auth.signout({ force: true });
    },
    onApiVersionError: (error) => {
      return handleApiVersionError(environment, error);
    },
  });

  const undoRedo = new UndoRedoStack();
  envLogger.debug({ undoRedo }, "Created UndoRedoStack");

  const environment: ClientEnvironment = {
    ...environmentBase,
    analytics,
    auth,
    clientId,
    inputMode,
    isLoading,
    isPendingUpdate,
    leader,
    logger,
    persistedDb,
    persistedDbWorker,
    router,
    serviceWorker,
    sessionStorage: sessionStorageService,
    sharedWorker,
    undoRedo,
  };

  if (persistedDb) {
    let isErrored = false;

    promiseTimeout(
      15_000,
      persistedDb.getSchemaVersion().then((version) => {
        environment.info.version.schema.actual = version;

        if (isErrored) {
          logger.warn("Eventually got persisted db schema version");
        }
      }),
    ).catch((error) => {
      logger.error({ error }, "Failed to get persisted db schema version");
      isErrored = true;
    });
  }

  // Log user ID changes and handle logout events.
  auth.currentUserId$.pipe(startWith(auth.getCurrentUserId), pairwise()).subscribe(([prevUserId, currentUserId]) => {
    logger.info({ currentUserId }, `CURRENT_USER_ID`);

    environment.info.currentUserId = currentUserId;
    sharedWorker?.onAuthChange();

    if (prevUserId && !currentUserId) {
      // If the user logs out on another tab, we want to reload this tab to reset the environment.
      // We clear pending updates so that we don't warn the user about unsaved changes (which might
      // prevent the page from being reloaded).
      isPendingUpdate.showPendingUpdatesWarning(false);
      window.location.href = "/login";
    }
  });

  // Looking at the production logs, it seems like it's possible some users have an invalid owner_organization_id
  // Cookie. The only place that I think a bug like this could reside would be the createUser logic. In some
  // scenarios, the ownerOrganizationId value associated with someone's login can change after they create an
  // account for the first time. We already migrated the ownerOrganizationId cookie in this case, but just to be
  // safe I refactored the logic to be safer by expiring any existing cookies when their account is created.
  // If any existing users were effected by an unknown bug here,
  // I don't see any issues there but, to be safe, I refactored the createUser logic
  // Looking at the `createUser` logic, it seems possible that someone might have an invalid ownerOrganizationId
  // value in certain scenerios after creating an account (this would only last until the user logs out). I've fixed that
  // potential issue. Here we are checking if the user's ownerOrganizationId is valid and signing them out of not.
  // It's possible that we can remove this code in the future.
  // -- John 10/8/24
  auth.currentUserId$
    .pipe(
      filter(isNonNullable),
      switchMap((currentUserId) =>
        combineLatest([
          environment.recordLoader.observeGetRecord("user_profile", currentUserId, { fetchStrategy: "cache-first" }),
          auth.currentUserOwnerOrganizationId$,
        ]),
      ),
      // We refetch the ownerOrganizationId (rather than using the emitted value) since there can be a slight delay
      // between auth change and the currentUserOwnerOrganizationId$ observable emitting.
      map(([[record]]) => [record?.owner_organization_id, auth.getCurrentUserOwnerOrganizationId()] as const),
      distinctUntilChanged(isEqual),
    )
    .subscribe(([expectedOwnerOrganizationId, cookieOwnerOrganizationId]) => {
      if (!expectedOwnerOrganizationId) return;
      if (expectedOwnerOrganizationId === cookieOwnerOrganizationId) return;
      auth.signout();
    });

  // Set the user context for Datadog logs.
  auth.currentUserId$
    .pipe(
      filter(isNonNullable),
      switchMap((currentUserId) => {
        if (!currentUserId) return of<Partial<RecordValue<"user_profile">>>({});

        return environment.recordLoader
          .observeGetRecord("user_profile", currentUserId, { fetchStrategy: "cache-first" })
          .pipe(
            map(([record]) => pick(record, ["id", "name"])),
            distinctUntilChanged(isEqual),
          );
      }),
    )
    .subscribe(({ id, name }) => {
      if (id) {
        datadogLogs.setUser({ id, name });
        datadogRum.setUser({ id, name });
      } else {
        datadogLogs.clearUser();
        datadogRum.clearUser();
      }
    });

  // When a user creates an account for the first time, their ownerOrganizationId value might change.
  auth.currentUserOwnerOrganizationId$.subscribe((currentOrgId) => {
    logger.info({ currentOrgId }, `current user's owner organization ${currentOrgId}`);
    sharedWorker?.onAuthChange();
  });

  KBarState.init();
  IsLoadingDialogState.init(environment);
  isPendingUpdate.init(environment);
  inputMode.init();
  router.init({ tanstackRouter });
  analytics.init().catch((error) => {
    logger.error({ error }, "error initializing analytics");
  });

  // See the e2e Page dialect's `waitForSyncServiceInitialSyncToComplete` method for how this is used.
  if (isE2ETest(config.appEnvironment)) {
    (environment as any).isInitialSyncComplete$ = SyncServiceProvider.isInitialSyncComplete$;
  }

  logger.notice({ environment, ...deviceMetadata }, "Created environment");

  return environment;
}
